import { Suspense, useEffect, useRef, useState } from 'react';
import { useUnit } from 'effector-react';
import {
  $initialAccountsStore,
  $initialCardsStore,
  $initialCurrenciesAvailableStore,
  $profileFeatureStore,
  $profileStore,
  $remoteConfigStore,
} from '@lib/store';
import { AuthApi } from '@shared/api';
import { AuthUtils, LocalAuthData } from '@shared/utils';
import { Public, Private } from '.';
import { PageContainer } from '@widgets';
import { Spinner } from '@shared/widgets';

export const Root = () => {
  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const initialCurrenciesAvailableStore = useUnit($initialCurrenciesAvailableStore.store);
  const remoteConfigStore = useUnit($remoteConfigStore.store);

  const [isShowSpinner, setIsShowSpinner] = useState<boolean>(false);
  const [isAuth, setIsAuth] = useState<boolean>(AuthUtils.isAuth());
  const hasInitialized = useRef<boolean>(false);

  const onCallbackAuthEvent = (isAuthTmp: boolean) => {
    setIsAuth(isAuthTmp);
    if (!isAuthTmp || hasInitialized.current) return;
    hasInitialized.current = true;
    Promise.allSettled([
      $profileStore.events.getProfile(),
      $profileFeatureStore.events.getProfileFeatures(),
      $initialAccountsStore.events.getAccounts(),
      $initialCardsStore.events.getCards(),
      $initialCurrenciesAvailableStore.events.getAvailableCurrencies(),
      $remoteConfigStore.events.getRemoteConfig(),
    ]);
  };

  useEffect(() => {
    onCallbackAuthEvent(isAuth);

    AuthUtils.eventIsAuth.on(AuthUtils.isAuthEventName, onCallbackAuthEvent);

    AuthUtils.init();

    if (!AuthUtils.isAuth()) {
      const userId = AuthUtils.getTempTokenForLogIn();
      if (userId) {
        setIsShowSpinner(true);
        AuthApi.authenticate({ tempToken: userId })
          .then((x) => {
            if (!x) return;
            AuthUtils.updateAuthData(x as LocalAuthData);
          })
          .catch(() => AuthUtils.logout({ showNotification: true }))
          .finally(() => setIsShowSpinner(false));
      } else {
        AuthUtils.redirectToLoginPage();
      }
    } else {
      AuthUtils.eventIsAuth.emit(AuthUtils.isAuthEventName, true);
    }

    return () => {
      AuthUtils.eventIsAuth.off(AuthUtils.isAuthEventName, onCallbackAuthEvent);
      hasInitialized.current = false;
    };
  }, []);

  if (isShowSpinner) return <Spinner position />;
  if (isAuth && (profileFeatureStore.loading || initialCurrenciesAvailableStore.loading || remoteConfigStore.loading)) return <Spinner position />;
  if (isAuth)
    return (
      <Suspense fallback={<PageContainer />}>
        <Private />
      </Suspense>
    );
  return (
    <Suspense fallback={<></>}>
      <Public />
    </Suspense>
  );
};
